import { yupResolver } from "@hookform/resolvers/yup";
import {
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import MarketingConsent from "@/components/booking/marketingConsent";
import LoadingButton from "@/components/common/buttons/loadingButton";
import OutlinedButton from "@/components/common/buttons/outlinedButton";
import TextButton from "@/components/common/buttons/textButton";
import { ConfirmationCheckboxes } from "@/components/common/checkbox/confirmationCheckboxes";
import SectionHeader from "@/components/common/sectionHeader/sectionHeader";
import { ERROR_PRIMARY } from "@/config/mui/colorPalette";
import { useClientBookingContext } from "@/contexts/clientBookingContext";
import useAction from "@/hooks/misc/useAction";
import { BookingStep } from "@/types/bookingTypes";
import { formatPhoneNumber } from "@/utils/formatPhoneNumber";
import AgreementCheckbox from "../agreementCheckbox";
import BookingInfoCard from "../bookingInfoCard";
import { CancellationPolicy } from "../cancellationPolicy";
import ClientAffirmationLabel from "../clientAffirmationLabel";
import DepositAdditionalInfo from "../depositAdditionalInfo";

type ReturningClientForm = {
  comment: string;
  consentToCharge: boolean;
  ageConsent: boolean;
  marketingConsent: boolean;
};

const getSchema = (collectCardInformation: boolean) =>
  yup.object({
    comment: yup.string(),
    consentToCharge:
      collectCardInformation &&
      yup.boolean().isTrue("Please agree to the terms."),
    ageConsent: yup
      .boolean()
      .isTrue(
        "Confirm your age and consent to the Privacy and Telehealth terms."
      ),
    marketingConsent: yup
      .boolean()
      .isTrue("Please consent to the marketing terms."),
  });

export default function BookingReturningClient() {
  const {
    card,
    medspaData,
    clientDetails,
    customizationEnabled,
    customizationTextColor,
    depositsEnabled,
    collectCardInformation,
    onlineBookingDepositsV1Enabled,
    depositPaymentError,
    handleBookingSubmit,
  } = useClientBookingContext();

  const {
    cancellationFeePolicyEnabled,
    lateCancellationAdvanceHours,
    noShowFeeValue,
    allowDeposits,
    depositAmount,
    clientAffirmation,
  } = medspaData?.configuration || {};

  const customButtonTextColor = customizationEnabled
    ? customizationTextColor
    : undefined;

  const {
    control,
    watch,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<ReturningClientForm>({
    resolver: yupResolver(getSchema(collectCardInformation)),
    defaultValues: {
      comment: "",
      consentToCharge: false,
      ageConsent: false,
      marketingConsent: false,
    },
  });

  const bookVisit = async () => {
    await handleBookingSubmit({
      ...clientDetails,
      comment: getValues("comment") || "",
    });
  };

  const { handleAction, isLoading } = useAction(bookVisit);

  const buttonCopy = depositsEnabled
    ? "Pay deposit and book appointment"
    : "Book appointment";

  const checkboxesToWatch: (keyof ReturningClientForm)[] = [
    "marketingConsent",
    "ageConsent",
  ];
  if (collectCardInformation) {
    checkboxesToWatch.unshift("consentToCharge");
  }

  return (
    <BookingInfoCard>
      <Typography
        variant="h3"
        sx={{
          color: "text.primary",
          pb: 3,
        }}
      >
        Review your contact info
      </Typography>
      <Stack>
        <PersonalInfoSection />
        {collectCardInformation && <CreditCardSection />}
        {depositPaymentError && (
          <Typography variant="labelSmall" color={ERROR_PRIMARY} mt={1}>
            Something is wrong with your credit card. Try different card.
          </Typography>
        )}
        {depositsEnabled && (
          <DepositAdditionalInfo depositAmount={depositAmount} />
        )}
        <form>
          <Stack
            sx={{
              width: "100%",
              mt: collectCardInformation ? 3 : 0,
              mb: collectCardInformation ? -2 : 0,
            }}
            spacing={1}
          >
            <Controller
              name="comment"
              control={control}
              render={({ field }) => (
                <TextField
                  multiline
                  minRows={3}
                  slotProps={{
                    htmlInput: { maxLength: 300 },
                  }}
                  type="visitReason"
                  placeholder="Visit reason"
                  {...field}
                  error={!!errors.comment}
                  helperText={
                    errors.comment ? (errors.comment.message as string) : ""
                  }
                />
              )}
            />
            <ConfirmationCheckboxes
              getValues={getValues}
              setValue={setValue}
              checkboxNames={checkboxesToWatch}
            >
              {collectCardInformation && (
                <Controller
                  name="consentToCharge"
                  control={control}
                  render={({ field }) => (
                    <>
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...field}
                            checked={field.value}
                            sx={{
                              color: errors.consentToCharge ? "error.main" : "",
                              alignSelf: "flex-start",
                            }}
                          />
                        }
                        label={
                          onlineBookingDepositsV1Enabled ? (
                            <ClientAffirmationLabel
                              cancellationFeePolicyEnabled={
                                cancellationFeePolicyEnabled
                              }
                              customClientAffirmation={clientAffirmation}
                              lateCancellationHours={
                                lateCancellationAdvanceHours
                              }
                              noShowFeeValue={noShowFeeValue}
                              depositAmount={depositAmount}
                              allowDeposits={allowDeposits}
                            />
                          ) : (
                            <CancellationPolicy
                              lateCancellationHours={
                                lateCancellationAdvanceHours
                              }
                              noShowFeeValue={noShowFeeValue}
                            />
                          )
                        }
                      />
                      <FormHelperText error={!!errors.consentToCharge}>
                        {(errors.consentToCharge?.message as string) ?? ""}
                      </FormHelperText>
                    </>
                  )}
                />
              )}

              <MarketingConsent
                name="marketingConsent"
                control={control}
                errors={errors}
              />

              <AgreementCheckbox
                control={control}
                errors={errors}
                medspaId={medspaData.id}
                name="ageConsent"
              />
            </ConfirmationCheckboxes>
          </Stack>
        </form>
        <LoadingButton
          fullWidth
          sx={{ mt: 3 }}
          variant="contained"
          onClick={handleSubmit(handleAction)}
          loading={isLoading}
          disabled={
            (collectCardInformation
              ? !watch("consentToCharge") || !card
              : false) ||
            !watch("ageConsent") ||
            !watch("marketingConsent")
          }
        >
          <Typography
            variant="buttonDefault"
            sx={{
              color: customButtonTextColor,
            }}
          >
            {buttonCopy}
          </Typography>
        </LoadingButton>
      </Stack>
    </BookingInfoCard>
  );
}

function PersonalInfoSection() {
  const {
    clientDetails: { firstName, lastName, phone, email },
    setStep,
  } = useClientBookingContext();

  return (
    <>
      <SectionHeader
        title="Personal data"
        action={
          <TextButton
            sx={{ minWidth: "fit-content" }}
            onClick={() => setStep(BookingStep.RETURNING_CLIENT_EDIT_DETAILS)}
          >
            Edit
          </TextButton>
        }
      />
      <Grid container spacing={1} sx={{ mb: 3 }}>
        <Grid size={3}>
          <Typography sx={{ color: "gray.main" }} noWrap>
            Name
          </Typography>
        </Grid>
        <Grid size={9}>
          <Typography>
            {firstName} {lastName}
          </Typography>
        </Grid>
        <Grid size={3}>
          <Typography sx={{ color: "gray.main" }} noWrap>
            Phone
          </Typography>
        </Grid>
        <Grid size={9}>
          <Typography>{formatPhoneNumber(phone)}</Typography>
        </Grid>
        <Grid size={3}>
          <Typography sx={{ color: "gray.main" }} noWrap>
            Email
          </Typography>
        </Grid>
        <Grid size={9}>
          <Typography sx={{ wordBreak: "break-word" }}>{email}</Typography>
        </Grid>
      </Grid>
    </>
  );
}

function CreditCardSection() {
  const { card, depositPaymentError, setStep } = useClientBookingContext();

  return (
    <>
      <SectionHeader
        title="Credit card data"
        action={
          card && (
            <TextButton
              sx={{ minWidth: "fit-content" }}
              onClick={() => setStep(BookingStep.RETURNING_CLIENT_EDIT_CARD)}
            >
              Use different card
            </TextButton>
          )
        }
      />
      {card ? (
        <Grid container spacing={1}>
          <Grid size={{ xs: 4, lg: 3 }}>
            <Typography sx={{ color: "gray.main" }} noWrap>
              Card number
            </Typography>
          </Grid>
          <Grid
            size={{ xs: 8, lg: 9 }}
            color={depositPaymentError && ERROR_PRIMARY}
          >
            <Typography>**** **** **** {card.last4}</Typography>
          </Grid>
        </Grid>
      ) : (
        <Stack spacing={1}>
          <Typography variant="paragraphSmall">
            A credit or debit card is required to book your appointment
          </Typography>
          <OutlinedButton
            onClick={() => setStep(BookingStep.RETURNING_CLIENT_EDIT_CARD)}
            text="Add credit card"
            withIcon={false}
            sx={{ height: 40 }}
          />
        </Stack>
      )}
    </>
  );
}
